import React from 'react'
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
} from '@react-pdf/renderer'
import { Patient } from 'fhir/r4'
import {
  invoiceDateTimeFormat,
  readableDateTime,
  getTotalAge,
} from '../../../utils/dateUtils'
import { upComingAppointmentType } from './patientsDashboard'
import {
  dietString,
  followUpdate,
  illnessHistory,
  signatureText,
  thopIdentifier,
  opCaseSheetLabels,
  painScoreLabels,
} from '../constants'
import medunitedLogo from '../../../assets/images/medunited.png'
import noHurts from '../../../assets/icons/noHurts.png'
import hurtsLittle from '../../../assets/icons/hurtsLittle.png'
import hurtsLot from '../../../assets/icons/hurtsLot.png'
import hurtsWorst from '../../../assets/icons/hurtsWorst.png'
import hurtsMore from '../../../assets/icons/hurtsMore.png'
import hurtsLittleMore from '../../../assets/icons/HurtsLittleMore.png'
import { patientConstants } from '../../lms/constants'

const getLogoUrl = () => {
  return medunitedLogo
}

type PatientProps = {
  patient: Patient
  historyList: any
  examinationList: any
  vitalsList: any
  diagnosisList: any
  labOrderList: any
  radiologyList: any
  visitsList: any
  upcomingAppointments?: upComingAppointmentType
  locationAddress?: any
}

interface historyDataProps {
  [key: string]: string
}

const styles = StyleSheet.create({
  page: {
    padding: 15,
    paddingTop: 10,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerBorder: {
    borderBottom: '2px solid #FF6B35',
    paddingVertical: 3,
    marginBottom: 5,
  },
  logo: {
    width: 80,
    height: 40,
    objectFit: 'contain',
  },
  addressArea: {
    flex: 1,
    paddingHorizontal: 15,
  },
  addressText: {
    fontSize: 9,
    marginBottom: 2,
    color: '#333',
  },
  addressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  icon: {
    width: 10,
    height: 10,
    marginRight: 5,
  },
  barcode: {
    width: 100,
    height: 40,
    marginTop: 5,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#2372C0',
    marginLeft: 15,
  },
  patientInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 3,
    backgroundColor: '#f5f5f5',
    padding: 8,
    borderRadius: 4,
  },
  infoItem: {
    width: '33.33%',
    marginBottom: 5,
  },
  infoItemFull: {
    width: '100%',
    marginBottom: 5,
  },
  infoLabel: {
    fontSize: 8,
    color: '#666',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#000',
    flexWrap: 'wrap',
    wordBreak: 'break-word',
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2372C0',
    marginTop: 10,
    marginBottom: 5,
    borderBottom: '1px solid #ccc',
    paddingBottom: 3,
  },
  subHeader: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
    marginBottom: 4,
  },
  contentBox: {
    backgroundColor: '#f9f9f9',
    padding: 6,
    borderRadius: 3,
    marginBottom: 5,
    fontSize: 9,
    minHeight: 20,
  },
  contentText: {
    fontSize: 9,
    lineHeight: 1.4,
    flexWrap: 'wrap',
    wordBreak: 'break-word',
  },
  painScoreContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 5,
    padding: 5,
    flexWrap: 'wrap',
  },
  painItem: {
    alignItems: 'center',
    width: '16%',
  },
  painNumber: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  painText: {
    fontSize: 7,
    textAlign: 'center',
    marginBottom: 3,
  },
  checkbox: {
    width: 10,
    height: 10,
    border: '1.5px solid #333',
    marginTop: 2,
    backgroundColor: '#fff',
    borderRadius: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxInner: {
    width: 5,
    height: 5,
    backgroundColor: '#000',
    borderRadius: 2.5,
  },
  checkboxSvg: {
    width: 10,
    height: 10,
  },
  personalHistoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
  },
  historyItem: {
    width: '50%',
    marginBottom: 5,
    paddingRight: 10,
  },
  historyLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  checkboxGroup: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  checkboxWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
  },
  checkboxText: {
    fontSize: 8,
    marginLeft: 4,
  },
  vitalsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
  },
  vitalInput: {
    width: '33.33%',
    marginBottom: 5,
    paddingRight: 10,
  },
  vitalLabel: {
    fontSize: 8,
    color: '#666',
    marginBottom: 3,
  },
  vitalValue: {
    fontSize: 9,
    backgroundColor: '#fff',
    padding: 4,
    border: '1px solid #ddd',
    borderRadius: 2,
  },
  vitalsValue: {
    fontSize: 9,
    backgroundColor: '#fff',
    padding: 4,
    border: '1px solid #ddd',
    borderRadius: 2,
  },
  checkboxRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
  },
  examCheckbox: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '33.33%',
    marginBottom: 5,
  },
  diagnosisGrid: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 5,
    flexWrap: 'wrap',
  },
  diagnosisBox: {
    flex: 1,
    minWidth: '45%',
  },
  diagnosisLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 4,
    color: '#2372C0',
  },
  diagnosisContent: {
    backgroundColor: '#f9f9f9',
    padding: 6,
    borderRadius: 3,
    fontSize: 8,
    minHeight: 40,
  },
  signatureArea: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    borderTop: '1px solid #ccc',
    paddingTop: 10,
  },
  followupContainer: {
    flex: 1,
  },
  signatureContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  signatureImage: {
    width: 80,
    height: 40,
    marginBottom: 5,
  },
  signatureLine: {
    width: 150,
    borderTop: '1px solid #000',
    marginTop: 5,
    marginBottom: 3,
  },
  signatureText: {
    fontSize: 8,
    textAlign: 'center',
  },
  twoColumnLayout: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  columnLeft: {
    flex: 1,
    minWidth: '45%',
  },
  columnRight: {
    flex: 1,
    minWidth: '45%',
  },
  infoBox: {
    marginBottom: 5,
  },
  infoBoxLabel: {
    fontSize: 8,
    color: '#666',
    marginBottom: 2,
  },
  infoBoxValue: {
    fontSize: 9,
    backgroundColor: '#f9f9f9',
    padding: 4,
    borderRadius: 2,
    flexWrap: 'wrap',
  },
  ribbon: {
    height: 2,
    marginBottom: 1,
    flexDirection: 'row',
  },
  orangeLine: {
    height: 2,
    backgroundColor: '#FF6B35',
    width: '50%',
  },
  blueLine: {
    height: 2,
    backgroundColor: '#2372C0',
    width: '50%',
  },
  painIcon: {
    width: 20,
    height: 20,
    marginBottom: 2,
  },
  headerLogoContainer: {
    width: '30%',
    alignItems: 'flex-start',
  },
  headerTextContainer: {
    width: '70%',
    alignItems: 'flex-start',
  },
  headerAddress: {
    fontSize: 8,
    marginTop: 2,
    textAlign: 'center',
    color: '#333',
  },
  headerContactRow: {
    flexDirection: 'row',
    marginTop: 2,
    justifyContent: 'center',
    marginLeft: 20,
  },
  headerContactText: {
    fontSize: 7,
    marginRight: 15,
    color: '#666',
  },
  headerContactTextLast: {
    fontSize: 7,
    color: '#666',
  },
  followupText: {
    fontSize: 9,
  },
})

function toCamelCase(str: string) {
  return str
    .replace(/(^\w|[A-Z]|\b\w)/g, (word, index) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase()
    })
    .replace(/\s+/g, '')
    .replaceAll('/', '')
}

export const getFilterdData = (dataObj: any) => {
  const Data: historyDataProps = {}
  if (dataObj) {
    dataObj.forEach((item: any) => {
      const key = toCamelCase(item?.code?.coding?.[0]?.display)
      Data[key] = item?.valueString ? item?.valueString : ''
    })
  }
  return Data
}

const Checkbox = ({ checked }: { checked: boolean }) => (
  <View style={styles.checkbox}>
    {checked && (
      <Svg style={styles.checkboxSvg} viewBox='0 0 10 10'>
        <Path
          d='M1.5 5 L4 7.5 L8.5 2.5'
          stroke='#000'
          strokeWidth='1.2'
          fill='none'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </Svg>
    )}
  </View>
)

const PatientHistory = ({ diet, sleep, bowelAndBladder }: any) => (
  <View style={styles?.personalHistoryGrid}>
    <View style={styles?.historyItem}>
      <Text style={styles?.historyLabel}>{opCaseSheetLabels.diet}</Text>
      <View style={styles?.checkboxGroup}>
        <View style={styles?.checkboxWrapper}>
          <Checkbox checked={diet === dietString?.veg} />
          <Text style={styles?.checkboxText}>{opCaseSheetLabels?.veg}</Text>
        </View>
        <View style={styles?.checkboxWrapper}>
          <Checkbox checked={diet === dietString?.mixed} />
          <Text style={styles?.checkboxText}>{opCaseSheetLabels?.mixed}</Text>
        </View>
      </View>
    </View>

    <View style={styles?.historyItem}>
      <Text style={styles?.historyLabel}>{opCaseSheetLabels?.sleep}</Text>
      <View style={styles?.checkboxGroup}>
        <View style={styles?.checkboxWrapper}>
          <Checkbox checked={sleep === dietString?.adequate} />
          <Text style={styles?.checkboxText}>
            {opCaseSheetLabels?.adequate}
          </Text>
        </View>
        <View style={styles?.checkboxWrapper}>
          <Checkbox checked={sleep === dietString?.inadequate} />
          <Text style={styles?.checkboxText}>
            {opCaseSheetLabels?.inadequate}
          </Text>
        </View>
      </View>
    </View>

    <View style={styles?.historyItem}>
      <Text style={styles?.historyLabel}>
        {opCaseSheetLabels?.bowelBladder}
      </Text>
      <View style={styles?.checkboxGroup}>
        <View style={styles?.checkboxWrapper}>
          <Checkbox checked={bowelAndBladder === dietString?.regular} />
          <Text style={styles?.checkboxText}>{opCaseSheetLabels?.regular}</Text>
        </View>
        <View style={styles?.checkboxWrapper}>
          <Checkbox checked={bowelAndBladder === dietString?.irregular} />
          <Text style={styles?.checkboxText}>
            {opCaseSheetLabels?.irregular}
          </Text>
        </View>
      </View>
    </View>
  </View>
)

// Render full text without truncation
const renderFullText = (text: string) => {
  if (!text)
    return (
      <Text style={styles?.contentText}>{opCaseSheetLabels?.defaultValue}</Text>
    )

  return <Text style={styles?.contentText}>{text}</Text>
}

const wongPainArray = [
  {
    value: 'nh',
    label: painScoreLabels?.noHurt,
    number: '0',
    icon: noHurts,
  },
  {
    value: 'hlb',
    label: painScoreLabels?.hurtsLittleBit,
    number: '2',
    icon: hurtsLittle,
  },
  {
    value: 'hlm',
    label: painScoreLabels?.hurtsLittleMore,
    number: '4',
    icon: hurtsLittleMore,
  },
  {
    value: 'hem',
    label: painScoreLabels?.hurtsEvenMore,
    number: '6',
    icon: hurtsMore,
  },
  {
    value: 'hwl',
    label: painScoreLabels?.hurtsWholeLot,
    number: '8',
    icon: hurtsLot,
  },
  {
    value: 'hw',
    label: painScoreLabels?.hurtsWorst,
    number: '10',
    icon: hurtsWorst,
  },
]

const OpCaseSheetPDF = ({
  patient,
  visitsList,
  historyList,
  vitalsList,
  examinationList,
  diagnosisList,
  locationAddress,
}: PatientProps) => {
  const { name, gender, birthDate } = patient
  const { drName, department, date, signature } = visitsList
  const getRegistrationNo = (patient: Patient | undefined) => {
    const identifier = patient?.identifier
    if (identifier) {
      const regNo = identifier?.find((data: any) =>
        data?.system?.includes(patientConstants?.patientIdentifier) ? data : ''
      )
      const thopId = identifier?.find((data: any) =>
        data?.system?.includes(thopIdentifier) ? data : ''
      )
      return { regNo, thopId }
    }
    return ''
  }

  const getVitalData = (vitalsList: any) => {
    const Data: historyDataProps = {}
    if (vitalsList) {
      vitalsList?.forEach((item: any) => {
        const key = toCamelCase(item?.code?.coding?.[0]?.code)
        Data[key] = item?.valueString ? item?.valueString : ''
      })
    }
    return Data
  }

  const getBmi = () => {
    let BMI
    if (Height && Weight) {
      BMI = (parseInt(Weight) / parseInt(Height) / parseInt(Height)) * 10000
      BMI = parseInt(BMI?.toFixed(2))
      if (BMI) {
        if (BMI < 18.5) {
          BMI = `Underweight - ${BMI}`
        } else if (BMI >= 18.5 && BMI < 25) {
          BMI = `Normal - ${BMI}`
        } else if (BMI >= 25 && BMI < 30) {
          BMI = `Overweight - ${BMI}`
        } else {
          BMI = `Obese - ${BMI}`
        }
      }
    }
    return BMI
  }

  const patientName = (() => {
    const firstName = name?.[0]?.given?.[0] || ''
    const middleName = name?.[0]?.given?.[1] || ''
    const lastName = name?.[0]?.family || ''
    const fullName = `${firstName} ${middleName} ${lastName}`.trim()
    return fullName || opCaseSheetLabels?.defaultValue
  })()

  const age = birthDate
    ? getTotalAge(new Date(birthDate)) || opCaseSheetLabels?.defaultValue
    : opCaseSheetLabels?.defaultValue

  const DrName = (() => {
    const firstName = drName?.[0]?.given?.[0] || ''
    const middleName = drName?.[0]?.given?.[1] || ''
    const lastName = drName?.[0]?.family || ''
    const fullName = `${firstName} ${middleName} ${lastName}`?.trim()
    return fullName || opCaseSheetLabels?.notAvailable
  })()

  const registrationNoData: any = getRegistrationNo(patient)
  const registrationNo: any =
    registrationNoData?.regNo?.value ||
    registrationNoData?.thopId?.value ||
    opCaseSheetLabels?.defaultValue

  const currentDate =
    invoiceDateTimeFormat(new Date()) || opCaseSheetLabels?.defaultValue
  const appointmentDate =
    readableDateTime(date) || opCaseSheetLabels?.defaultValue

  const historyData = getFilterdData(historyList)
  const {
    AlcoholicHistory,
    BowelAndBladder,
    ChiefComplaints,
    Clubbing,
    Cyanosis,
    Diet,
    Edema,
    FamilyHistory,
    Height,
    HistoryOfDrugAllergies,
    HistoryOfPresentIllness,
    HistoryOfPreviousSurgeries,
    Icterus,
    Lymphadenopathy,
    ObstetricHistory,
    OtherRelevantHistory,
    Pallor,
    PastHistory,
    Sleep,
    SmokingHistory,
    Weight,
    WongBakerPainScore,
  } = historyData

  const vitalData = getVitalData(vitalsList)
  const { Diastolic, Posture, Pulse, Resprate, Spo2, Systolic, Temparature } =
    vitalData

  const examinationListData = getFilterdData(examinationList)
  const {
    LocalExamination,
    CardiovascularSystem,
    RespiratorySystem,
    NervousSystem,
    PerAbdomen,
  } = examinationListData

  const diagnosisData = getFilterdData(diagnosisList)
  const {
    ProvisionalDiagnosis,
    DifferentialDiagnosis,
    InvestigationsDiagnostics,
    FinalDiagnosis,
    TreatmentPlan,
  } = diagnosisData

  const logoUrl = getLogoUrl()

  return (
    <Document>
      <Page size='A4' style={styles?.page} wrap>
        {/* Header Ribbon */}
        <View style={styles?.ribbon}>
          <View style={styles?.orangeLine} />
          <View style={styles?.blueLine} />
        </View>

        {/* Header Section */}
        <View style={styles?.headerBorder}>
          <View style={styles?.header}>
            <View style={styles?.headerLogoContainer}>
              <Image src={logoUrl} style={styles?.logo} />
            </View>
            <View style={styles?.headerTextContainer}>
              <Text style={styles?.title}>
                {opCaseSheetLabels?.outPatientCaseSheet}
              </Text>
              <Text style={styles?.headerAddress}>
                {locationAddress?.address?.line?.[0] ||
                  opCaseSheetLabels?.defaultValue}
              </Text>
              <View style={styles?.headerContactRow}>
                <Text style={styles?.headerContactText}>
                  {locationAddress?.telecom?.[0]?.value ||
                    opCaseSheetLabels?.defaultValue}
                </Text>
                <Text style={styles?.headerContactTextLast}>
                  {locationAddress?.telecom?.[1]?.value ||
                    opCaseSheetLabels?.defaultValue}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Patient Info */}
        <View style={styles?.patientInfoGrid}>
          <View style={styles?.infoItem}>
            <Text style={styles?.infoLabel}>
              {opCaseSheetLabels?.patientUHID}
            </Text>
            <Text style={styles?.infoValue}>{registrationNo}</Text>
          </View>
          <View style={styles?.infoItem}>
            <Text style={styles?.infoLabel}>{opCaseSheetLabels?.name}</Text>
            <Text style={styles?.infoValue}>{patientName}</Text>
          </View>
          <View style={styles?.infoItem}>
            <Text style={styles?.infoLabel}>{opCaseSheetLabels?.gender}</Text>
            <Text style={styles?.infoValue}>
              {gender || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.infoItem}>
            <Text style={styles?.infoLabel}>{opCaseSheetLabels?.age}</Text>
            <Text style={styles?.infoValue}>{age}</Text>
          </View>
          <View style={styles?.infoItem}>
            <Text style={styles?.infoLabel}>{opCaseSheetLabels?.date}</Text>
            <Text style={styles?.infoValue}>{appointmentDate}</Text>
          </View>
          <View style={styles?.infoItem}>
            <Text style={styles?.infoLabel}>
              {opCaseSheetLabels?.department}
            </Text>
            <Text style={styles?.infoValue}>
              {department || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.infoItemFull}>
            <Text style={styles?.infoLabel}>
              {opCaseSheetLabels?.doctorName}
            </Text>
            <Text style={styles?.infoValue}>
              {DrName === opCaseSheetLabels?.notAvailable
                ? DrName
                : `${opCaseSheetLabels?.doctorPrefix} ${DrName}`}
            </Text>
          </View>
        </View>

        {/* Wong-Baker Pain Score */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.wongBakerPainScore}
        </Text>
        <View style={styles?.painScoreContainer}>
          {wongPainArray?.map((item) => (
            <View key={item?.value} style={styles?.painItem}>
              <Image src={item?.icon} style={styles?.painIcon} />
              <Text style={styles?.painText}>{item?.label}</Text>
              <Checkbox checked={WongBakerPainScore === item?.value} />
            </View>
          ))}
        </View>

        {/* History & Examination */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.historyExamination}
        </Text>

        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.chiefComplaints}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(ChiefComplaints)}
        </View>
        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.historyPresentingIllness}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(HistoryOfPresentIllness)}
        </View>

        <Text style={styles?.subHeader}>{opCaseSheetLabels?.pastHistory}</Text>
        <View style={styles?.contentBox}>{renderFullText(PastHistory)}</View>

        {/* Personal History */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.personalHistory}
        </Text>
        <View style={styles?.twoColumnLayout}>
          <View style={styles?.columnLeft}>
            <PatientHistory
              diet={Diet}
              sleep={Sleep}
              bowelAndBladder={BowelAndBladder}
            />
          </View>
          <View style={styles?.columnRight}>
            <View style={styles?.infoBox}>
              <Text style={styles?.infoBoxLabel}>
                {opCaseSheetLabels?.smokingHistory}
              </Text>
              <Text style={styles?.infoBoxValue}>
                {SmokingHistory || opCaseSheetLabels?.defaultValue}
              </Text>
            </View>
            <View style={styles?.infoBox}>
              <Text style={styles?.infoBoxLabel}>
                {opCaseSheetLabels?.alcoholHistory}
              </Text>
              <Text style={styles?.infoBoxValue}>
                {AlcoholicHistory || opCaseSheetLabels?.defaultValue}
              </Text>
            </View>
          </View>
        </View>

        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.drugAllergies}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(HistoryOfDrugAllergies)}
        </View>

        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.otherRelevantHistory}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(OtherRelevantHistory)}
        </View>

        {/* Family History */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.familyHistory}
        </Text>
        <View style={styles?.contentBox}>{renderFullText(FamilyHistory)}</View>

        {/* Obstetric History */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.obstetricHistory}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(ObstetricHistory)}
        </View>

        {/* History of Previous Surgeries */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.previousSurgeries}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(HistoryOfPreviousSurgeries)}
        </View>

        {/* General Physical Examination */}
        <Text style={styles?.sectionHeader}>
          {opCaseSheetLabels?.generalPhysicalExam}
        </Text>
        <View style={styles?.vitalsGrid}>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>
              {opCaseSheetLabels?.heightCM}
            </Text>
            <Text style={styles?.vitalsValue}>
              {Height || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>
              {opCaseSheetLabels?.weightKG}
            </Text>
            <Text style={styles?.vitalsValue}>
              {Weight || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>{opCaseSheetLabels?.bmi}</Text>
            <Text style={styles?.vitalsValue}>
              {getBmi() || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
        </View>

        <View style={styles?.checkboxRow}>
          {[
            {
              label: illnessHistory?.Pallor,
              checked: Pallor === illnessHistory?.yes,
            },
            {
              label: illnessHistory?.Icterus,
              checked: Icterus === illnessHistory?.yes,
            },
            {
              label: illnessHistory?.Cyanosis,
              checked: Cyanosis === illnessHistory?.yes,
            },
            {
              label: illnessHistory?.Clubbing,
              checked: Clubbing === illnessHistory?.yes,
            },
            {
              label: illnessHistory?.Lymphadenopathy,
              checked: Lymphadenopathy === illnessHistory?.yes,
            },
            {
              label: illnessHistory?.Edema,
              checked: Edema === illnessHistory?.yes,
            },
          ]?.map((item, idx) => (
            <View key={idx} style={styles?.examCheckbox}>
              <Checkbox checked={item?.checked} />
              <Text style={styles?.checkboxText}>{item?.label}</Text>
            </View>
          ))}
        </View>

        {/* Vitals */}
        <Text style={styles?.sectionHeader}>{opCaseSheetLabels?.vitals}</Text>
        <View style={styles?.vitalsGrid}>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>{opCaseSheetLabels?.pulse}</Text>
            <Text style={styles?.vitalsValue}>
              {Pulse || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>
              {opCaseSheetLabels?.bloodPressure}
            </Text>
            <Text style={styles?.vitalsValue}>
              {Systolic
                ? `${Systolic}/${Diastolic}`
                : opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>
              {opCaseSheetLabels?.temperature}
            </Text>
            <Text style={styles?.vitalsValue}>
              {Temparature || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>{opCaseSheetLabels?.posture}</Text>
            <Text style={styles?.vitalsValue}>
              {Posture || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>
              {opCaseSheetLabels?.respiratoryRate}
            </Text>
            <Text style={styles?.vitalsValue}>
              {Resprate || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
          <View style={styles?.vitalInput}>
            <Text style={styles?.vitalLabel}>{opCaseSheetLabels?.spo2}</Text>
            <Text style={styles?.vitalsValue}>
              {Spo2 || opCaseSheetLabels?.defaultValue}
            </Text>
          </View>
        </View>

        {/* Systemic Examination */}
        {(LocalExamination ||
          CardiovascularSystem ||
          RespiratorySystem ||
          NervousSystem ||
          PerAbdomen) && (
          <>
            <Text style={styles?.sectionHeader}>
              {opCaseSheetLabels?.systemicExamination}
            </Text>

            <Text style={styles?.subHeader}>
              {opCaseSheetLabels?.localExamination}
            </Text>
            <View style={styles?.contentBox}>
              {renderFullText(LocalExamination)}
            </View>

            <Text style={styles?.subHeader}>
              {opCaseSheetLabels?.cardiovascularSystem}
            </Text>
            <View style={styles?.contentBox}>
              {renderFullText(CardiovascularSystem)}
            </View>

            <Text style={styles?.subHeader}>
              {opCaseSheetLabels?.respiratorySystem}
            </Text>
            <View style={styles?.contentBox}>
              {renderFullText(RespiratorySystem)}
            </View>

            <Text style={styles?.subHeader}>
              {opCaseSheetLabels?.nervousSystem}
            </Text>
            <View style={styles?.contentBox}>
              {renderFullText(NervousSystem)}
            </View>

            <Text style={styles?.subHeader}>
              {opCaseSheetLabels?.perAbdomen}
            </Text>
            <View style={styles?.contentBox}>{renderFullText(PerAbdomen)}</View>
          </>
        )}

        {/* Diagnosis */}
        <Text style={styles?.sectionHeader}>{opCaseSheetLabels.diagnosis}</Text>
        <View style={styles?.diagnosisGrid}>
          <View style={styles?.diagnosisBox}>
            <Text style={styles?.diagnosisLabel}>
              {opCaseSheetLabels?.provisionalDiagnosis}
            </Text>
            <View style={styles?.diagnosisContent}>
              {renderFullText(ProvisionalDiagnosis)}
            </View>
          </View>
          <View style={styles?.diagnosisBox}>
            <Text style={styles?.diagnosisLabel}>
              {opCaseSheetLabels?.finalDiagnosis}
            </Text>
            <View style={styles?.diagnosisContent}>
              {renderFullText(FinalDiagnosis)}
            </View>
          </View>
        </View>

        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.differentialDiagnosis}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(DifferentialDiagnosis)}
        </View>

        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.investigationsDiagnostics}
        </Text>
        <View style={styles?.contentBox}>
          {renderFullText(InvestigationsDiagnostics)}
        </View>

        <Text style={styles?.subHeader}>
          {opCaseSheetLabels?.treatmentPlan}
        </Text>
        <View style={styles?.contentBox}>{renderFullText(TreatmentPlan)}</View>

        {/* Signature Area */}
        <View style={styles?.signatureArea}>
          <View style={styles?.followupContainer}>
            <Text style={styles?.followupText}>
              {followUpdate}: {currentDate}
            </Text>
          </View>

          <View style={styles?.signatureContainer}>
            {signature && (
              <Image style={styles?.signatureImage} src={signature} />
            )}
            <View style={styles?.signatureLine} />
            <Text style={styles?.signatureText}>{signatureText}</Text>
          </View>
        </View>

        {/* Footer Ribbon */}
        <View style={styles?.ribbon} fixed>
          <View style={styles?.orangeLine} />
          <View style={styles?.blueLine} />
        </View>
      </Page>
    </Document>
  )
}

export default OpCaseSheetPDF
